import React, { useState, useEffect } from 'react';
import { getWebhookEvents, exportAdminData } from '../api/userAPI';

const WebhookEventLog = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [sortField, setSortField] = useState('created');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedEvent, setExpandedEvent] = useState(null);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWebhookEvents();
      setEvents(data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load webhook events:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleExport = async (format) => {
    try {
      setExporting(true);
      await exportAdminData(format);
    } catch (err) {
      setError(err.message);
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedAndFilteredEvents = () => {
    let filtered = events;

    // Apply filters
    if (filterType !== 'all') {
      filtered = filtered.filter(event => event.type === filterType);
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter(event => event.status === filterStatus);
    }

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'created') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed': return '#28a745';
      case 'failed': return '#dc3545';
      case 'pending': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const getEventTypeIcon = (type) => {
    if (type.includes('succeeded') || type.includes('paid') || type.includes('completed')) {
      return '✓';
    }
    if (type.includes('failed') || type.includes('error')) {
      return '✗';
    }
    if (type.includes('created')) {
      return '+';
    }
    if (type.includes('updated')) {
      return '↻';
    }
    if (type.includes('deleted') || type.includes('cancelled')) {
      return '-';
    }
    return '•';
  };

  const toggleEventDetails = (eventId) => {
    setExpandedEvent(expandedEvent === eventId ? null : eventId);
  };

  if (loading) {
    return (
      <div className="webhook-event-log">
        <div className="loading">Loading webhook events...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="webhook-event-log">
        <div className="error">
          <h3>Error Loading Events</h3>
          <p>{error}</p>
          <button onClick={loadEvents} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const sortedAndFilteredEvents = getSortedAndFilteredEvents();

  return (
    <div className="webhook-event-log">
      <div className="events-header">
        <h2>Webhook Event Log</h2>
        <div className="header-actions">
          <button 
            onClick={loadEvents} 
            className="refresh-button"
            disabled={loading}
          >
            Refresh
          </button>
          <div className="export-buttons">
            <button 
              onClick={() => handleExport('csv')} 
              className="export-button csv"
              disabled={exporting}
            >
              Export CSV
            </button>
            <button 
              onClick={() => handleExport('json')} 
              className="export-button json"
              disabled={exporting}
            >
              Export JSON
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="type-filter">Event Type:</label>
          <select 
            id="type-filter"
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="checkout.session.completed">Checkout Completed</option>
            <option value="payment_intent.succeeded">Payment Succeeded</option>
            <option value="charge.succeeded">Charge Succeeded</option>
            <option value="customer.subscription.created">Subscription Created</option>
            <option value="customer.subscription.updated">Subscription Updated</option>
            <option value="customer.subscription.deleted">Subscription Deleted</option>
            <option value="invoice.paid">Invoice Paid</option>
            <option value="customer.created">Customer Created</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Status:</label>
          <select 
            id="status-filter"
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="processed">Processed</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        <div className="events-count">
          Showing {sortedAndFilteredEvents.length} of {events.length} events
        </div>
      </div>

      {/* Events Table */}
      <div className="events-table-container">
        <table className="events-table">
          <thead>
            <tr>
              <th 
                className={`sortable ${sortField === 'created' ? sortDirection : ''}`}
                onClick={() => handleSort('created')}
              >
                Timestamp {sortField === 'created' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className={`sortable ${sortField === 'type' ? sortDirection : ''}`}
                onClick={() => handleSort('type')}
              >
                Event Type {sortField === 'type' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className={`sortable ${sortField === 'status' ? sortDirection : ''}`}
                onClick={() => handleSort('status')}
              >
                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className={`sortable ${sortField === 'user_id' ? sortDirection : ''}`}
                onClick={() => handleSort('user_id')}
              >
                User ID {sortField === 'user_id' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredEvents.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-events">
                  No events found matching the current filters
                </td>
              </tr>
            ) : (
              sortedAndFilteredEvents.map((event) => (
                <React.Fragment key={event.id}>
                  <tr className="event-row">
                    <td className="timestamp">
                      {formatDate(event.created)}
                    </td>
                    <td className="event-type">
                      <span className="event-icon">{getEventTypeIcon(event.type)}</span>
                      {event.type}
                    </td>
                    <td className="status">
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(event.status) }}
                      >
                        {event.status}
                      </span>
                    </td>
                    <td className="user-id">
                      {event.data?.user_id || 'N/A'}
                    </td>
                    <td className="actions">
                      <button 
                        onClick={() => toggleEventDetails(event.id)}
                        className="toggle-details-button"
                      >
                        {expandedEvent === event.id ? 'Hide' : 'View'} Details
                      </button>
                    </td>
                  </tr>
                  {expandedEvent === event.id && (
                    <tr className="event-details-row">
                      <td colSpan="5" className="event-details">
                        <div className="details-content">
                          <h4>Event Details</h4>
                          <div className="details-grid">
                            <div className="detail-item">
                              <strong>Event ID:</strong> {event.id}
                            </div>
                            <div className="detail-item">
                              <strong>Provider:</strong> {event.provider || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Amount:</strong> {event.data?.amount ? `$${(event.data.amount / 100).toFixed(2)}` : 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Currency:</strong> {event.data?.currency || 'N/A'}
                            </div>
                            <div className="detail-item">
                              <strong>Subscription ID:</strong> {event.data?.subscription_id || 'N/A'}
                            </div>
                          </div>
                          <div className="raw-data">
                            <h5>Raw Data:</h5>
                            <pre>{JSON.stringify(event, null, 2)}</pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <style jsx>{`
        .webhook-event-log {
          padding: 20px;
          background: var(--bg-primary, #f8f9fa);
          min-height: 100vh;
        }

        .events-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid var(--border-color, #e9ecef);
        }

        .events-header h2 {
          margin: 0;
          color: var(--text-primary, #333);
        }

        .header-actions {
          display: flex;
          gap: 15px;
          align-items: center;
        }

        .refresh-button, .export-button {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .refresh-button {
          background: var(--primary-color, #007bff);
          color: white;
        }

        .refresh-button:hover:not(:disabled) {
          background: var(--primary-hover, #0056b3);
        }

        .export-button {
          background: var(--success-color, #28a745);
          color: white;
        }

        .export-button.json {
          background: var(--info-color, #17a2b8);
        }

        .export-button:hover:not(:disabled) {
          opacity: 0.9;
        }

        .export-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .filters-section {
          background: white;
          padding: 20px;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          margin-bottom: 20px;
          display: flex;
          gap: 20px;
          align-items: center;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .filter-group label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-secondary, #666);
        }

        .filter-select {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #e9ecef);
          border-radius: 6px;
          font-size: 14px;
          background: white;
        }

        .events-count {
          margin-left: auto;
          font-size: 14px;
          color: var(--text-secondary, #666);
        }

        .events-table-container {
          background: white;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .events-table {
          width: 100%;
          border-collapse: collapse;
        }

        .events-table th {
          background: var(--light-bg, #f8f9fa);
          padding: 15px;
          text-align: left;
          font-weight: 600;
          color: var(--text-primary, #333);
          border-bottom: 2px solid var(--border-color, #e9ecef);
        }

        .events-table th.sortable {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s ease;
        }

        .events-table th.sortable:hover {
          background: var(--border-color, #e9ecef);
        }

        .events-table td {
          padding: 15px;
          border-bottom: 1px solid var(--border-color, #e9ecef);
          vertical-align: middle;
        }

        .event-row:hover {
          background: var(--light-bg, #f8f9fa);
        }

        .timestamp {
          font-family: monospace;
          font-size: 14px;
          color: var(--text-secondary, #666);
        }

        .event-type {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .event-icon {
          font-size: 16px;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          color: white;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
        }

        .user-id {
          font-family: monospace;
          font-size: 14px;
          color: var(--text-secondary, #666);
        }

        .toggle-details-button {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          background: var(--primary-color, #007bff);
          color: white;
          cursor: pointer;
          font-size: 12px;
          transition: background-color 0.2s ease;
        }

        .toggle-details-button:hover {
          background: var(--primary-hover, #0056b3);
        }

        .event-details-row {
          background: var(--light-bg, #f8f9fa);
        }

        .event-details {
          padding: 0;
        }

        .details-content {
          padding: 20px;
        }

        .details-content h4 {
          margin: 0 0 15px 0;
          color: var(--text-primary, #333);
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 10px;
          margin-bottom: 20px;
        }

        .detail-item {
          font-size: 14px;
        }

        .detail-item strong {
          color: var(--text-primary, #333);
        }

        .raw-data {
          margin-top: 20px;
        }

        .raw-data h5 {
          margin: 0 0 10px 0;
          color: var(--text-primary, #333);
        }

        .raw-data pre {
          background: var(--bg-primary, #f8f9fa);
          padding: 15px;
          border-radius: 6px;
          font-size: 12px;
          overflow-x: auto;
          border: 1px solid var(--border-color, #e9ecef);
        }

        .no-events {
          text-align: center;
          color: var(--text-secondary, #666);
          font-style: italic;
          padding: 40px;
        }

        .loading, .error {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary, #666);
        }

        .error {
          color: var(--danger-color, #dc3545);
        }

        .retry-button {
          margin-top: 10px;
          padding: 8px 16px;
          background: var(--primary-color, #007bff);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }

        @media (max-width: 768px) {
          .events-header {
            flex-direction: column;
            gap: 15px;
            align-items: stretch;
          }

          .header-actions {
            justify-content: center;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .events-count {
            margin-left: 0;
            text-align: center;
          }

          .events-table-container {
            overflow-x: auto;
          }

          .events-table {
            min-width: 600px;
          }

          .details-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default WebhookEventLog;
